import { apiErrorMessage } from '@agos/api-client'
import {
  getGetSettingsDeliveryQueryKey,
  useGetSettingsDelivery,
  usePutSettingsDelivery,
} from '@agos/api-client/platform'
import { T, tt } from '@agos/shared'
import { Spinner, toast, BUTTON_PRIMARY, cn} from '@agos/ui'
import { useQueryClient } from '@tanstack/react-query'
import { Check, Loader2, MapPin, Save } from 'lucide-react'
import { useEffect, useState } from 'react'

import { AddressField } from '@agos/ui'

/**
 * The depot address - one field, and it is load-bearing.
 *
 * The depot is where drivers start and return, so it is the origin every
 * delivery's kilometres are measured from - which means it is also the origin of
 * every driver's per-kilometre pay. A wrong depot does not fail; it quietly
 * mis-pays everybody.
 *
 * **A section rather than a page since §9.302.** `/delivery-settings` is now a hub and this is its
 * first tab, so the `PageHeader` moved up to `DeliveryHubPage` - two of them stacked would have read
 * *Nastavenia rozvozu* twice. The card's own heading, *Východiskový bod (depot)*, still names it.
 *
 * Ported from legacy `pages/delivery-settings.tsx`.
 */

const SAVED_FLASH_MS = 2000

export function DepotSection() {
  const queryClient = useQueryClient()
  const saveDelivery = usePutSettingsDelivery()
  const { data, isLoading } = useGetSettingsDelivery()

  const [depotAddress, setDepotAddress] = useState('')
  const [loadedFor, setLoadedFor] = useState<unknown>(null)
  const [isSaving, setIsSaving] = useState(false)
  const [justSaved, setJustSaved] = useState(false)

  // Adjust state when the input changed, rather than syncing from an effect:
  // the field is the user's while they are typing, and the server's before that.
  if (data !== loadedFor) {
    setLoadedFor(data)
    setDepotAddress((data as { depotAddress?: string } | undefined)?.depotAddress ?? '')
  }

  useEffect(() => {
    if (!justSaved) return
    const timer = setTimeout(() => setJustSaved(false), SAVED_FLASH_MS)
    return () => clearTimeout(timer)
  }, [justSaved])

  const save = async () => {
    setIsSaving(true)
    setJustSaved(false)
    try {
      // The generated hook, now that the spec describes this PUT. It used to be a hand-rolled
      // `customFetch` with a body nothing checked.
      await saveDelivery.mutateAsync({ data: { depotAddress: depotAddress.trim() } })
      await queryClient.invalidateQueries({ queryKey: getGetSettingsDeliveryQueryKey() })
      setJustSaved(true)
      toast({ title: tt('Uložené'), description: tt('Nastavenia rozvozu boli uložené') })
    } catch (caught) {
      toast({
        variant: 'destructive',
        title: tt('Chyba'),
        description: apiErrorMessage(caught, tt('Nepodarilo sa uložiť nastavenia')),
      })
    } finally {
      setIsSaving(false)
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Spinner />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="rounded-xl border border-border bg-card">
        <div className="px-5 py-4 border-b border-border bg-muted/30 rounded-t-xl">
          <h2 className="font-semibold text-foreground flex items-center gap-2">
            <MapPin className="w-4 h-4 text-primary" />
            <T>Východiskový bod (depot)</T>
          </h2>
          <p className="text-xs text-muted-foreground mt-1">
            <T>
              Adresa, odkiaľ rozvozári vyštartujú a kam sa vracajú. Používa sa na výpočet celkových
              km.
            </T>
          </p>
        </div>

        <div className="p-5 space-y-4">
          <div>
            <AddressField
              label={tt('Adresa depota')}
              value={depotAddress}
              onChange={setDepotAddress}
              placeholder={tt('Napr. M.R. Štefánika 191/3 Šurany')}
            />
            <p className="text-xs text-muted-foreground mt-1.5">
              <T>
                Zadajte presnú adresu vrátane ulice, čísla a mesta. Adresa sa použije pre výpočet
                vzdialeností.
              </T>
            </p>
          </div>

          <div className="flex justify-end pt-2">
            <button
              type="button"
              onClick={() => void save()}
              disabled={isSaving || !depotAddress.trim()}
              className={cn('inline-flex items-center gap-2 px-5 py-2.5 rounded-lg', BUTTON_PRIMARY, 'text-sm disabled:cursor-not-allowed')}
            >
              {isSaving ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : justSaved ? (
                <Check className="w-4 h-4" />
              ) : (
                <Save className="w-4 h-4" />
              )}
              {isSaving ? tt('Ukladám...') : justSaved ? tt('Uložené') : tt('Uložiť')}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
